import { FontAwesomeIcon as FontAwesomeIconNative } from '@fortawesome/vue-fontawesome';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const FontAwesomeIcon = tsx.ofType().convert(FontAwesomeIconNative);
/**
 * A simple wrapper around `font-awesome-icon` that allows styling and includes
 * the icon name in the class list of the icon for testing purposes.
 *
 * The properties scaleX, and scaleY use the css transform to scale the icon in
 * the specified dimension.
 *
 * Please note that transforms overwrite each other, so for instance in the case
 * of collapseable items, we rotate the icon using a transform. In this case the
 * scaleX and scaleY properties would create a conflict.
 *
 * See styling icons here: https://fontawesome.com/docs/web/use-with/vue/style
 * All of the native styling option are available in this wrapper, but will
 * generate a type error.
 */
export default tsx.componentFactoryOf().create({
    name: 'cg-icon',
    functional: true,
    props: {
        icon: p.ofType().required,
        spin: p(Boolean).default(false),
        /** Scale the icon using a specific scale. */
        scale: p(Number).default(1),
        /**
         * Force the style property to be in object form.
         * Note, it will still be retrieved from data.
         */
        style: p.ofType().optional,
        /** Overwrite the scale of the icon, using custom scaleX. */
        scaleX: p(Number).default(1),
        /** Overwrite the scale of the icon, using custom scaleY. */
        scaleY: p(Number).default(1),
        // Fontawesome native props
        /** Number between 0, 360 to signify the degrees of rotation. */
        rotation: p.ofType().optional,
        flip: p.ofType().optional,
        beat: p(Boolean).optional,
        'beat-fade': p(Boolean).optional,
        bounce: p(Boolean).optional,
        fade: p(Boolean).optional,
        shake: p(Boolean).optional,
        'spin-reverse': p(Boolean).optional,
        'spin-pulse': p(Boolean).optional,
        border: p(Boolean).optional,
        /** Wrap text around an icon with this utility. */
        pull: p.ofType().optional,
        /**
         * Scale icons up or down, use grow-# and shrink-# with any arbitrary value, including decimals.
         * Move icons up, down, left, or right, use up-#, down-#, left-#, and right-#.
         * Rotate or flip icons use any combination of flip-v, flip-h, or rotate-#.
         */
        transform: p(String).optional,
        /** Layer two icons but have the inner icon cut out from the icon below. */
        mask: p.ofType().optional,
        /** For DUOTONE icons only, swap the opacity on the layers. */
        'swap-opacity': p(Boolean).optional,
        counter: p(Boolean).optional,
        /** The value displayed for the counter prop. */
        value: p(Number).optional,
        /** Position of the counter prop, relative to the icon. */
        position: p.ofType().optional,
        inverse: p(Boolean).optional,
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render(h, ctx) {
        const { icon, spin, scale, scaleX, scaleY, rotation, flip, beat, 'beat-fade': beatFade, bounce, fade, shake, 'spin-reverse': spinReverse, 'spin-pulse': spinPulse, border, pull, transform, mask, 'swap-opacity': swapOpacity, counter, value, position, inverse, } = ctx.props;
        /**
         * Get the style for this icon.
         *
         * @returns The style for the icon to scale it properly, below the standard scale.
         */
        function getStyle() {
            let scaleStyle = {};
            if (scale !== 1) {
                // @ts-expect-error: The root instance of vue is
                // different, but typescript doesn't know that.
                const root = ctx.parent.$root;
                if (root.isSafari) {
                    scaleStyle = {
                        height: `${scale}em`,
                        width: `${scale}em`,
                    };
                }
                else if (scaleX !== 1 || scaleY !== 1 || scale !== 1) {
                    scaleStyle = { 'font-size': `${scale}em` };
                }
            }
            if (scaleX !== 1 || scaleY !== 1) {
                scaleStyle = {
                    ...scaleStyle,
                    transform: `scaleX(${scaleX}) scaleY${scaleY}`,
                };
            }
            return scaleStyle;
        }
        /**
         * Render the icon specified using the style and classes.
         *
         * @returns A icon node.
         */
        function renderIcon() {
            const classer = [icon, ctx.data.class];
            if (ctx.data.staticClass != null) {
                classer.push(ctx.data.staticClass);
            }
            return (<FontAwesomeIcon icon={icon} attrs={ctx.data.attrs} on={ctx.data.on} class={classer} spin={spin} style={getStyle()} rotation={rotation} flip={flip} beat={beat} beat-fade={beatFade} bounce={bounce} fade={fade} shake={shake} spin-reverse={spinReverse} spin-pulse={spinPulse} border={border} pull={pull} transform={transform} mask={mask} swap-opacity={swapOpacity} counter={counter} value={value} position={position} inverse={inverse}/>);
        }
        /**
         * Wrap in a span with the style property, then render the icon.
         *
         * @returns A styled icon node.
         */
        function styledIcon() {
            if (ctx.data.style != null) {
                return <span style={ctx.data.style}>{renderIcon()}</span>;
            }
            return renderIcon();
        }
        /** Wrap in a span with the static style if need be, should not happen often. */
        return ctx.data.staticStyle != null ? (<span style={ctx.data.staticStyle}>{styledIcon()}</span>) : (styledIcon());
    },
});
